import { Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ListColumn } from '../../../interfaces/base/list-column';
import { fadeInRightAnimation } from '../../../../@fury/animations/fade-in-right.animation';
import { fadeInUpAnimation } from '../../../../@fury/animations/fade-in-up.animation';
import { EMPTY, Subject } from 'rxjs';
import { ApiService } from '../../../services/base/api.service';
import { User } from '../../../interfaces/routering/user';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { AuthenticationService } from '../../../services/base/authentication.service';
import { MatDialog } from '@angular/material/dialog';
import { IndexButton } from '../../../interfaces/base/IndexButton';
import { TaskWebhookService } from '../../../services/tasks/task-webhook.service';
import { Task } from '../../../interfaces/task/task';
import { OverviewService } from '../../../services/overview/overview.service';
import { FormBuilder } from '@angular/forms';
import { TaskPanelService } from '../../../services/tasks/task-panel.service';
import { TaskService } from '../../../services/tasks/task.service';
import { Location } from '@angular/common';
import { Tile } from '../../../interfaces/routering/tile';
import { TileService } from '../../../services/routering/tile.service';
import { ServerRouteProperties } from '../../../interfaces/base/server-route-properties';

@Component({
  selector: 'fury-tile-tasks-index',
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.scss'],
  animations: [fadeInRightAnimation, fadeInUpAnimation],
})
export class ViewerComponent implements OnInit, OnDestroy {

  @Input() tileId: number = null;
  public tile: Tile = null;

  public isLoading: boolean = false;

  public columns: ListColumn[] = [
    {title: 'Type', name: 'basic_registration_id', type: 'select', sortable: true, filterable: true},
    {title: 'Taak', name: 'number', type: 'string', sortable: true, filterable: true},
    {title: 'Deadline', name: 'handled_days', type: null, sortable: false, filterable: false},
    {title: 'Prioriteit', name: 'priority_id', type: 'select', sortable: true, filterable: true},
  ] as ListColumn[];

  public form = this.fb.group({
    number: <string>null,
  });

  public topNavButtons: IndexButton[] = [];
  public lastOpenTaskId: number = null;

  public dataSource: MatTableDataSource<Task> | null;

  private matPaginatorRef: MatPaginator = null;
  private matSortRef: MatSort = null;

  @ViewChild(MatPaginator, {static: true}) set paginator(paginator: MatPaginator) {
    this.matPaginatorRef = paginator;
    this.api.matPaginator = paginator;
  }

  @ViewChild(MatSort, {static: true}) set sort(sort: MatSort) {
    this.matSortRef = sort;
    this.api.matSort = sort;
  }

  public routeParams: ServerRouteProperties = {
    active: '',
    direction: '',
    page: 1,
    length: this.api.pageSize,
    filters: [],
  };

  public clearFilters: EventEmitter<any> = new EventEmitter<any>();

  private data$: Subject<null> = new Subject<null>();
  private destroy$: Subject<null> = new Subject<null>();

  constructor(private router: Router,
              private fb: FormBuilder,
              private location: Location,
              private tileService: TileService,
              public taskPanelService: TaskPanelService,
              public taskWebhookService: TaskWebhookService,
              public dialog: MatDialog,
              public api: ApiService,
              public overviewService: OverviewService,
              public authenticationService: AuthenticationService,
              public taskService: TaskService,
  ) {
  }

  get visibleColumns() {
    return this.columns.map((c: ListColumn): string => c.name);
  }

  ngOnInit(): void {
    this.api.filterPageName = null;
    this.taskPanelService.nextTaskId.pipe(takeUntil(this.destroy$)).subscribe((taskId: number): void => {
      console.log(taskId);
      this.tileId = taskId;

      if (taskId) {
        this.location.replaceState(this.location.path(false).replace(/\d+/g, '') + taskId);

        this.tileService.get(this.tileId).pipe(takeUntil(this.destroy$)).subscribe(
          (tile: Tile): void => {
            this.tile = tile;
          },
          (error: any): void => {
            console.log(error);
          }
        );
      }

      this.updateButtons();

      this.getData();
    });

    this.matSortRef.sort({
      id: 'priority_id',
      start: 'asc',
      disableClear: false,
    });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof NavigationEnd && (['/tegel'].indexOf(event.url) !== -1)) {
        this.api.matPaginator = this.matPaginatorRef;
        this.api.matSort = this.matSortRef;
      }
    });

    this.dataSource = new MatTableDataSource();

    this.authenticationService.user$.pipe(takeUntil(this.destroy$)).subscribe((user: User): void => {
      if (user) {
        this.taskWebhookService.init(this.dataSource, this.matPaginatorRef);
      }
    });

    this.isLoading = true;

    this.api.filterUpdated.pipe(takeUntil(this.destroy$)).subscribe((updatePageIndex: boolean): void => {
      if (updatePageIndex) {
        if (typeof this.paginator !== 'undefined') {
          this.paginator.pageIndex = 0;
        }
      }

      this.getData();
    });

    this.api.generalHttpError.pipe(takeUntil(this.destroy$)).subscribe((result: boolean): void => {
      if (result) {
        this.isLoading = false;
      }
    });

    this.data$.pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        this.isLoading = true;

        return this.overviewService.tasks(this.tileId, {...this.routeParams, ...this.form.value}).pipe(
          takeUntil(this.destroy$),
          map((response: any): void => {
            if (!this.api.isErrorResponse(response)) {
              this.dataSource.data = response;
            }

            this.isLoading = false;
          }),
          catchError(() => {
            this.isLoading = false;
            return EMPTY;
          })
        );
      }),
      catchError(() => {
        this.isLoading = false;
        return EMPTY;
      })
    ).subscribe();

    this.getData();
  }

  getData(): void {
    this.data$.next();
  }

  clearValue(field: string): void {
    this.form.get(field).setValue(null);
  }

  closeEditor(): void {
    this.taskPanelService.close();
  }

  updateButtons(): void {
    this.overviewService.listOfTileIdsUpdated.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      // handle Previous button
      const previousButtonIndex: number = this.topNavButtons.findIndex((d: IndexButton): boolean => d.id === 'previous-task'),
        previousId: number = this.overviewService.getPreviousId(this.tileId);

      if (!previousId && previousButtonIndex !== -1) {
        this.topNavButtons.splice(previousButtonIndex, 1);
      }

      if (previousButtonIndex === -1 && previousId) {
        this.topNavButtons.splice(0, 0, {
          id: 'previous-task',
          tooltip: 'Vorige',
          icon: 'chevron_left',
          color: 'white',
          functionOrRoute: this.taskPanelService.open.bind(this.taskPanelService, previousId),
        });
      } else if (previousButtonIndex !== -1 && previousId) {
        this.topNavButtons[previousButtonIndex].functionOrRoute = this.taskPanelService.open.bind(this.taskPanelService, previousId);
      }

      // handle Next button
      const nextButtonIndex: number = this.topNavButtons.findIndex((d: IndexButton): boolean => d.id === 'next-task'),
        nextId: number = this.overviewService.getNextId(this.tileId);

      if (!nextId && nextButtonIndex !== -1) {
        this.topNavButtons.splice(nextButtonIndex, 1);
      }

      if (nextButtonIndex === -1 && nextId) {
        const previousButtonIndex2: number = this.topNavButtons.findIndex((d: IndexButton): boolean => d.id === 'previous-task');
        this.topNavButtons.splice((previousButtonIndex2 > -1 ? previousButtonIndex2 + 1 : 0), 0, {
          id: 'next-task',
          tooltip: 'Volgende',
          icon: 'chevron_right',
          iconPosition: 'right',
          color: 'white',
          functionOrRoute: this.taskPanelService.open.bind(this.taskPanelService, nextId),
        });
      } else if (nextButtonIndex !== -1 && nextId) {
        this.topNavButtons[nextButtonIndex].functionOrRoute = this.taskPanelService.open.bind(this.taskPanelService, nextId);
      }
    });
  }

  getTileUsers(tile: Tile): string {
    return tile.users.map((user: User): string => user.name).join(', ');
  }


  setSort(data: Sort): void {
    this.routeParams.active = (data.direction !== '' ? (typeof data.active !== 'undefined' && data.active !== '' ? data.active : '') : '');
    this.routeParams.direction = data.direction;

    if (typeof this.matPaginatorRef !== 'undefined' && this.matPaginatorRef !== null && typeof this.matSortRef !== 'undefined' && this.matSortRef !== null) {
      if (this.matSortRef.active === null && data.active !== null && data.active !== '') {
        this.matSortRef.active = data.active;
        this.matSortRef.direction = data.direction;
        this.matSortRef._stateChanges.next();
      }
      if (this.matSortRef.active !== null && this.matSortRef.active !== data.active) {
        this.routeParams.page = 1;
        this.matPaginatorRef.pageIndex = 0;
      }
    }

    this.getData();
  }

  setPagination(data: PageEvent | any, emitEvent: boolean = true): void {
    if (typeof this.matPaginatorRef !== 'undefined' && this.matPaginatorRef !== null) {
      this.matPaginatorRef.pageIndex = data.pageIndex;
      this.matPaginatorRef.pageSize = data.pageSize;
      this.matPaginatorRef.length = data.length;

      this.api.setGoToPage.emit(data.pageIndex);
    }
    this.routeParams.page = data.pageIndex + 1;
    this.routeParams.length = data.pageSize;

    this.getData();
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.taskPanelService.close();
    this.taskWebhookService.destroy();

    this.isLoading = false;
  }
}
